import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { paths } from '../../config/paths';
import { publicAsset } from '../../config/publicAsset';
import { useAuth } from '../../contexts/AuthProvider';
import { apiFetchWithoutBearer } from '../../config/apiBase';
import { postAuthLogin } from '../../api/auth/auth';
import { useSystemPopup } from '../../components/common/SystemPopup';
import { isTemporaryPasswordPlain, passwordForLoginApi } from '../../utils/form/tempPassword';
import { sha256Base64 } from '../../utils/crypto/sha256Base64';
import { resolveLoginReturnPath } from '../../utils/auth/loginReturnPath';
import { applyPostLoginNavigation, RESUME_OFFLINE_APPLY_STATE_KEY } from '../../utils/auth/postLoginNavigation';
import { plantCookiePanel } from '../../utils/auth/cookiePanel';
import {
  getLoginMaxLengthExceededMessage,
  LOGIN_ID_MAX,
  LOGIN_ID_MIN,
  LOGIN_PW_MAX,
  LOGIN_PW_MIN,
  parseLoginUserIdInput,
  parseLoginPasswordInput,
} from '../../utils/form/loginInput';

const MSG_LOGIN_ID_HANGUL = '영문 및 숫자만 입력 가능합니다.';

const MSG_INVALID_LOGIN = '입력하신 아이디와 비밀번호가 올바르지 않습니다.';
const MSG_INVALID_CAPTCHA = '보안문자를 확인해 주세요.';
/** 3.0 else 분기 — not-exist·except-panel 동일 문구 */
const MSG_NOT_EXIST =
  '존재하지 않는 아이디입니다\n아이디형식: 영문 소문자 + 숫자 (4~12자리)';

function parseFailCnt(search) {
  const params = new URLSearchParams(search);
  const parsed = Number(params.get('loginFailCnt'));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

/** API·쿼리스트링에서 온 실패 횟수 (JSON 숫자/문자열 모두 허용) */
function coerceLoginFailCnt(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null;
}

/** 로그인 오류 응답에서 서버가 내려준 실패 횟수 추출 */
function pickLoginFailCntFromJson(json) {
  if (!json || typeof json !== 'object') return null;
  const d = json.data;
  if (d && typeof d === 'object' && !Array.isArray(d)) {
    const v = d.loginFailCnt ?? d.login_fail_cnt;
    const n = coerceLoginFailCnt(v);
    if (n !== null) return n;
  }
  return coerceLoginFailCnt(json.loginFailCnt);
}

export default function AuthLogin() {
  const { login, isLoggedIn, sessionReady } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { open: openSystemPopup } = useSystemPopup();
  const captchaObjectUrlRef = useRef(null);
  const formRef = useRef(null);
  const loginIdComposingRef = useRef(false);
  const loginPasswordComposingRef = useRef(false);

  const { search, state: locationState } = location;

  const [loginId, setLoginId] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginFailCnt, setLoginFailCnt] = useState(() => parseFailCnt(search));
  const [captchaUrl, setCaptchaUrl] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loginReturnPath = useMemo(
    () => resolveLoginReturnPath(locationState?.from),
    [locationState?.from],
  );

  useEffect(() => {
    if (!sessionReady || !isLoggedIn) return;
    navigate(paths.home, { replace: true });
  }, [isLoggedIn, sessionReady, navigate]);

  const blocked = sessionReady && isLoggedIn;

  const loadCaptcha = useCallback(async () => {
    try {
      const res = await apiFetchWithoutBearer(`/api/auth/captcha?t=${Date.now()}`, { method: 'GET' });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (captchaObjectUrlRef.current) {
        URL.revokeObjectURL(captchaObjectUrlRef.current);
      }
      captchaObjectUrlRef.current = url;
      setCaptchaUrl(url);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    loadCaptcha();
    return () => {
      if (captchaObjectUrlRef.current) {
        URL.revokeObjectURL(captchaObjectUrlRef.current);
        captchaObjectUrlRef.current = null;
      }
    };
  }, [loadCaptcha]);

  // URL의 loginFailCnt는 "초기 진입"에만 반영. 매번 parseFailCnt(빈 문자열)=0 으로 덮어쓰면 API가 준 횟수가 사라짐.
  useEffect(() => {
    const p = new URLSearchParams(search);
    if (!p.has('loginFailCnt')) return;
    setLoginFailCnt(parseFailCnt(search));
  }, [search]);

  const clearPasswordField = useCallback(() => {
    setLoginPassword('');
  }, []);

  const clearCaptchaField = useCallback(() => {
    const el = formRef.current?.elements?.namedItem?.('captchaVal');
    if (el && 'value' in el) {
      el.value = '';
    }
  }, []);

  const handleCaptchaRefresh = useCallback(() => {
    clearCaptchaField();
    loadCaptcha();
  }, [clearCaptchaField, loadCaptcha]);

  const notifyLoginMaxLengthExceeded = useCallback(
    (maxLength) => {
      openSystemPopup({
        type: 'alert',
        content: <p className="strong-text">{getLoginMaxLengthExceededMessage(maxLength)}</p>,
      });
    },
    [openSystemPopup],
  );

  const notifyLoginIdInputRejection = useCallback(
    (rejectedHangul, rejectedLength) => {
      if (rejectedHangul) {
        openSystemPopup({
          type: 'alert',
          content: <p className="strong-text">{MSG_LOGIN_ID_HANGUL}</p>,
        });
        return;
      }
      if (rejectedLength) {
        notifyLoginMaxLengthExceeded(LOGIN_ID_MAX);
      }
    },
    [openSystemPopup, notifyLoginMaxLengthExceeded],
  );

  const applyLoginIdInput = useCallback(
    (raw) => {
      const { value, rejectedHangul, rejectedLength } = parseLoginUserIdInput(raw);
      notifyLoginIdInputRejection(rejectedHangul, rejectedLength);
      setLoginId(value);
    },
    [notifyLoginIdInputRejection],
  );

  const handleLoginIdChange = useCallback(
    (e) => {
      const raw = e.target.value;
      if (loginIdComposingRef.current) {
        setLoginId(raw);
        return;
      }
      applyLoginIdInput(raw);
    },
    [applyLoginIdInput],
  );

  const handleLoginIdCompositionEnd = useCallback(
    (e) => {
      loginIdComposingRef.current = false;
      applyLoginIdInput(e.currentTarget.value);
    },
    [applyLoginIdInput],
  );

  const applyLoginPasswordInput = useCallback(
    (raw) => {
      const { value, rejectedLength } = parseLoginPasswordInput(raw);
      if (rejectedLength) {
        notifyLoginMaxLengthExceeded(LOGIN_PW_MAX);
      }
      setLoginPassword(value);
    },
    [notifyLoginMaxLengthExceeded],
  );

  const handleLoginPasswordChange = useCallback(
    (e) => {
      const raw = e.target.value;
      if (loginPasswordComposingRef.current) {
        setLoginPassword(raw);
        return;
      }
      applyLoginPasswordInput(raw);
    },
    [applyLoginPasswordInput],
  );

  const handleLoginPasswordCompositionEnd = useCallback(
    (e) => {
      loginPasswordComposingRef.current = false;
      applyLoginPasswordInput(e.currentTarget.value);
    },
    [applyLoginPasswordInput],
  );

  const postLoginNavigate = useCallback(
    (data) => {
      applyPostLoginNavigation(data, navigate, loginReturnPath, {
        resumeOfflineApply: Boolean(locationState?.[RESUME_OFFLINE_APPLY_STATE_KEY]),
      });
    },
    [navigate, loginReturnPath, locationState],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    const form = e.currentTarget;
    const fd = new FormData(form);
    const loginIdVal = loginId.trim();
    const rawPw = loginPassword;
    const captchaVal = String(fd.get('captchaVal') || '').trim();

    if (!loginIdVal || !rawPw) {
      setSubmitError('아이디와 비밀번호를 입력해 주세요.');
      return;
    }
    if (loginIdVal.length < LOGIN_ID_MIN || loginIdVal.length > LOGIN_ID_MAX) {
      setSubmitError(`아이디는 ${LOGIN_ID_MIN}~${LOGIN_ID_MAX}자로 입력해 주세요.`);
      return;
    }
    if (!isTemporaryPasswordPlain(rawPw) && (rawPw.length < LOGIN_PW_MIN || rawPw.length > LOGIN_PW_MAX)) {
      setSubmitError(`비밀번호는 ${LOGIN_PW_MIN}~${LOGIN_PW_MAX}자로 입력해 주세요.`);
      return;
    }

    let passwordForApi;
    try {
      passwordForApi = await passwordForLoginApi(rawPw, sha256Base64);
    } catch {
      setSubmitError('비밀번호 처리 중 오류가 발생했습니다.');
      return;
    }

    setSubmitting(true);
    try {
      const env = await postAuthLogin({
        userId: loginIdVal,
        password: passwordForApi,
        captchaVal,
      });
      const json = env.raw ?? {};
      clearCaptchaField();

      if (env.httpStatus === 200 && env.code === 200 && env.data) {
        const d = env.data;
        plantCookiePanel(d.cookieAddr);
        login({
          accessToken: d.accessToken,
          refreshToken: d.refreshToken,
          user: {
            userId: d.userId,
            name: d.name,
            janPay: d.janPay,
            activeGrade: d.activeGrade,
            needsProfileUpdate: d.needsProfileUpdate,
            tempPassword: d.tempPassword,
            pwDay: d.pwDay,
            reWrite: d.reWrite,
            rewriteMode: d.rewriteMode,
            previousActiveGrade: d.previousActiveGrade,
            cellNo1: d.cellNo1,
            cellNo2: d.cellNo2,
            cellNo3: d.cellNo3,
          },
        });
        setLoginFailCnt(0);
        clearPasswordField();
        setLoginId('');
        postLoginNavigate(d);
        form.reset();
        return;
      }

      const data =
        json.data && typeof json.data === 'object' && !Array.isArray(json.data) ? json.data : {};
      const result = data.result ?? json.result;
      const nextFail = pickLoginFailCntFromJson(json);
      if (nextFail !== null) {
        setLoginFailCnt(nextFail);
      } else if (result === 'invalid-login' || result === 'invalid-captcha') {
        setLoginFailCnt((c) => c + 1);
      }
      clearPasswordField();
      if (result === 'invalid-captcha') {
        setSubmitError(json.message || MSG_INVALID_CAPTCHA);
        loadCaptcha();
      } else if (result === 'invalid-login') {
        setSubmitError(json.message || MSG_INVALID_LOGIN);
      } else if (result === 'temp-panel') {
        setSubmitError(
          `${json.message || '이메일 인증이 필요합니다.'}${data.email ? `\n${data.email}` : ''}`,
        );
      } else {
        // 3.0 else: not-exist·except-panel 등 동일 처리
        setSubmitError(MSG_NOT_EXIST);
      }
    } catch {
      clearPasswordField();
      setSubmitError('네트워크 오류로 로그인에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  if (blocked) {
    return null;
  }

  return (
    <>
      <h2 className="page-title">로그인</h2>

      <section className="auth-login">
        <p className="page-desc">
          환영합니다.<br />
          엠브레인 패널파워 패널 로그인 페이지입니다.<br />
          여러분의 의견이 세상을 움직입니다.
        </p>

        <form
          ref={formRef}
          className="auth-form-wrap auth-form-wrap__login"
          autoComplete="off"
          onSubmit={handleSubmit}
        >
          <div className="auth-form ty-gap-lg">
            {loginFailCnt === 0 && submitError && (
              <p className="box__info" role="alert" style={{ color: '#c0392b', whiteSpace: 'pre-line' }}>
                {submitError}
              </p>
            )}
            {(loginFailCnt > 0 && loginFailCnt < 5) && (
              <dl className="box__info bullet__dot" role="alert">
                {submitError && <dd className="login-fail-msg">{submitError}</dd>}
                <dt className="ir before i-info-red">로그인 {loginFailCnt}회 실패</dt>
                <dd>개인정보보호를 위해 로그인 5회 이상 실패 시 이용이 일시적으로 제한되오니 신중하게 입력해주시기 바랍니다.</dd>
                <dd>등록되지 않은 아이디이거나, 아이디 또는 비밀번호를 잘못 입력하셨습니다.</dd>
                <dd>패널파워 아이디는 ‘영문+숫자’, 비밀번호는 ‘영문+숫자/특수문자’ 조합으로 되어 있습니다.</dd>
              </dl>
            )}
            {(loginFailCnt > 0 && loginFailCnt >= 5) && (
              <dl className="box__info bullet__dot" role="alert">
                {submitError && <dd className="login-fail-msg">{submitError}</dd>}
                <dt className="ir before i-info-red">로그인 {loginFailCnt}회 실패</dt>
                <dd>개인정보 보호를 위해 하단의 자동입력방지문자를 함께 입력해주십시오.</dd>
                <dd>정보가 기억나지 않을 경우, 하단 아이디/비밀번호 찾기를 통해 정보를 확인해주시기 바랍니다.</dd>
              </dl>
            )}
            <div className="field">
              <label className="field__label" htmlFor="loginId">
                아이디
              </label>
              <input
                className="input"
                id="loginId"
                name="loginId"
                type="text"
                autoComplete="off"
                inputMode="text"
                value={loginId}
                minLength={LOGIN_ID_MIN}
                onChange={handleLoginIdChange}
                onCompositionStart={() => {
                  loginIdComposingRef.current = true;
                }}
                onCompositionEnd={handleLoginIdCompositionEnd}
                required
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="loginPassword">
                비밀번호
              </label>
              <input
                className="input"
                id="loginPassword"
                name="loginPassword"
                type="password"
                autoComplete="off"
                value={loginPassword}
                minLength={LOGIN_PW_MIN}
                onChange={handleLoginPasswordChange}
                onCompositionStart={() => {
                  loginPasswordComposingRef.current = true;
                }}
                onCompositionEnd={handleLoginPasswordCompositionEnd}
                required
              />
            </div>

            <div className="captcha">
              <p className="captcha__desc">자동입력 방지문자를 아래 이미지를 보이는대로 입력해 주세요</p>
              <div className="captcha__image">
                <span className="img-wrap">
                  {captchaUrl ? (
                    <img src={captchaUrl} alt="자동입력방지문자" width={200} height={80} />
                  ) : null}
                </span>
                <span className="buttons">
                  <button type="button" className="btn ir after i-refresh" onClick={handleCaptchaRefresh}>
                    새로고침
                  </button>
                </span>
              </div>
              <div className="captcha__input">
                <input
                  type="text"
                  name="captchaVal"
                  className="input full"
                  placeholder="보안문자를 입력하세요"
                  aria-label="자동입력 방지문자 입력"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="action-buttons">
              <button type="submit" className="btn btn--lg btn--primary" disabled={submitting}>
                {submitting ? '처리 중…' : '로그인'}
              </button>
            </div>
          </div>


          <nav className="links" aria-label="계정 도움말">
            <ul className="list">
              <li>
                <Link className="auth-form__link" to={paths.auth.findId}>아이디 찾기</Link>
              </li>
              <li>
                <Link className="auth-form__link" to={paths.auth.findPw}>비밀번호 찾기</Link>
              </li>
              <li>
                <Link className="auth-form__link" to={paths.auth.signup}>패널가입</Link>
              </li>
            </ul>
          </nav>
          <div className="box__noti flex-row mg-type-2">
            <img src={publicAsset('/images/auth/privacy-logo.png')} alt="개인정보보호우수사이트" className="info-logo" />
            <p className="font-sm weight-500">
              엠브레인 패널파워는 국내 조사회사 중 유일하게
              개인정보보호 우수사이트 인증을 받았으며 패널님의
              소중한 정보를 안전하게 관리하고 있습니다.
            </p>
          </div>
        </form>
      </section>
    </>
  );
}
